/**
 * 高中側邊欄資源與「科目」對應（依產品規格表）。
 *
 * - 國文欄為「V」：題庫光碟、教學大補帖光碟、教學PPT、課程計畫、影音資源、多元選修、
 *   期刊、網路資源（不含教材電子檔、大考及段考題目—國文欄為空白）。
 * - 大考及段考題目：除國文外各科（國文欄空白）。
 * - 教材電子檔：除國文外皆有。
 * - 教材 PDF／WORD：僅國文。
 * - 教材圖檔：除國文外皆有。
 */

/** 國文欄皆為「V」的列（不含教材電子檔、大考及段考題目） */
const CORE_EVERY_SUBJECT: readonly string[] = [
  '題庫光碟',
  '教學大補帖光碟',
  '教學PPT',
  '課程計畫',
  '影音資源',
  '多元選修',
  '期刊',
  '網路資源',
];

const CHINESE_ONLY: readonly string[] = [
  '教材PDF',
  '教材WORD',
  '升學-大考',
  '心智圖',
  '教學遊戲',
];

const MATH_SCIENCE_WORKSHEET_SUBJECTS: readonly string[] = [
  '數學',
  '物理',
  '化學',
  '生物',
  '地球科學',
];

/** 與 Layout orderMap[H] 一致，供依科目過濾後仍維持固定排序 */
export const HIGH_SCHOOL_SIDEBAR_ORDER: readonly string[] = [
  '題庫光碟',
  '教學大補帖光碟',
  '教材電子檔',
  '教材PDF',
  '教材WORD',
  '教學PPT',
  '課程計畫',
  '教材圖檔',
  '影音資源',
  '實境解題',
  '數位教學學習單',
  '多元選修',
  '大考及段考題目',
  '升學-大考',
  '期刊',
  '網路資源',
  '心智圖',
  '教學遊戲',
];

function buildAllowSet(subject: string): Set<string> | null {
  if (subject === '國文') {
    return new Set<string>([...CORE_EVERY_SUBJECT, ...CHINESE_ONLY]);
  }

  const set = new Set<string>([
    ...CORE_EVERY_SUBJECT,
    '大考及段考題目',
    '教材電子檔',
    '教材圖檔',
  ]);

  if (subject === '數學') {
    set.add('實境解題');
    set.add('數位教學學習單');
    return set;
  }

  if (MATH_SCIENCE_WORKSHEET_SUBJECTS.includes(subject)) {
    set.add('數位教學學習單');
    return set;
  }

  return set;
}

/**
 * 依目前選取之高中科目，從完整排序清單挑出應顯示的側邊欄項目。
 * 若科目不在規格表內，回傳 null（呼叫端可沿用原邏輯）。
 */
export function getHighSchoolSidebarAllowSet(
  subject: string | null,
): Set<string> | null {
  if (!subject) return null;
  return buildAllowSet(subject);
}

export function orderHighSchoolSidebarBySubject(
  subject: string | null,
  fallbackOrdered: string[],
): string[] | null {
  const allow = getHighSchoolSidebarAllowSet(subject);
  if (!allow) return null;

  const primary = HIGH_SCHOOL_SIDEBAR_ORDER.filter(k => allow.has(k));
  const merged: string[] = [...primary];
  for (const k of fallbackOrdered) {
    if (allow.has(k) && !merged.includes(k)) {
      merged.push(k);
    }
  }
  return merged;
}
